import { Button, Modal, PropagationNotice, WriteError } from '@shared/ui';

import {
    componentMembershipAlert,
    overrideConfirmCopy,
    type OverrideChange,
} from './override-confirm-copy';

import type { FlagGrant } from '../schemas/flag';
import type { FlagKind } from '../schemas/flag-type';

/**
 * The confirmation on a client's **first** override for a flag.
 *
 * Same shape and same words as `OverrideDialog`'s confirm step — it calls the
 * same `overrideConfirmCopy` (NOXSCRUM-1670), so a `salesforce` flag says here
 * exactly what it says there: the value is projected one-way into that
 * customer's own Salesforce org.
 *
 * Not a second confirmation contract, and deliberately not a stricter one.
 * `tenant-flag-row.ts` decides when this opens, and it opens on exactly the
 * case the flag screen stops for: creating the row. Toggling a row that exists
 * and reverting one to the default both write without asking, there and here.
 *
 * It also carries `D15` rule 3's alert: a flag a component granted is a flag
 * this client was sold as part of a bundle, and turning it off breaks part of
 * that bundle while the Components tab goes on reading Active. `tenant-flag-row.ts`
 * opens this dialog for that case as well as for the first override, which is
 * why it is rendered here rather than in a second dialog.
 *
 * `pending` is `null` while closed. Setting it opens the dialog *instead of*
 * writing — the switch's `onChange` no longer mutates, this dialog's confirm
 * button does, in the parent. Cancelling needs no rollback: the mutation was
 * never started, so the switch renders the value it already had.
 */
export function TenantOverrideConfirmDialog({
    flagKey,
    kind,
    grants,
    pending,
    isPending,
    error,
    onCancel,
    onConfirm,
}: {
    flagKey: string;
    kind: FlagKind;
    /** This row's grantors, for `D15` rule 3. Empty on a row with none. */
    grants: readonly FlagGrant[];
    pending: OverrideChange | null;
    isPending: boolean;
    error: Error | null;
    onCancel: () => void;
    onConfirm: () => void;
}) {
    const copy = pending ? overrideConfirmCopy(flagKey, kind, pending) : undefined;
    const componentAlert = pending
        ? componentMembershipAlert({ enabled: pending.enabled, grants })
        : undefined;

    return (
        <Modal
            open={pending !== null}
            onClose={() => {
                if (!isPending) onCancel();
            }}
            dismissable={!isPending}
            title={copy?.title ?? ''}
            description={copy?.description}
            footer={
                <>
                    <Button variant="ghost" onClick={onCancel} disabled={isPending}>
                        Cancel
                    </Button>
                    <Button disabled={isPending} onClick={onConfirm}>
                        {isPending ? 'Saving…' : 'Confirm'}
                    </Button>
                </>
            }
        >
            <div className="flex flex-col gap-4">
                {error && <WriteError error={error} />}
                {componentAlert !== undefined && (
                    <p
                        role="alert"
                        className="rounded-lg border border-warning-line bg-warning-surface px-3.5 py-2.5 text-[12.5px] leading-relaxed text-warning-strong"
                    >
                        {componentAlert}
                    </p>
                )}
                <PropagationNotice />
            </div>
        </Modal>
    );
}
